/**
 * bridge/location_controller.js — "location" bridge component (Stimulus + Strada).
 *
 * Capability-tier bridge component: one-shot precise location for job-site
 * capture / address autofill. Stimulus identifier "bridge--location"
 * (glob-derived from this subdir); Strada component name "location" MUST match
 * the iOS class var name (native/ios/Chippie/Bridge/LocationComponent.swift).
 *
 * Why it extends Strada's BridgeComponent: the iOS request is reached via
 * this.send("get", {}, callback), which only works on a connected bridge
 * component inside the iOS Hotwire Native shell. Extending BridgeComponent gives
 * us that send() transport AND the UA-gated connect() (it only connects in a
 * native shell).
 *
 * Three-way transport: rather than each caller branching on platform, this
 * controller registers a single location() impl set onto window.ChippieBridge
 * ({ ios, android, pwa }). The facade (chippie-bridge.js) owns platform
 * detection and picks the right impl per call. Each impl is async and resolves
 * to { latitude, longitude } (or rejects on failure / denial).
 *   - ios:     this.send("get", {}, msg) over Strada to LocationComponent.swift
 *   - android: window.ChippieNative.getLocation() (binder thread); the native
 *              side hands the result back by invoking the global callback
 *              window.__chippieLocationResult(latitude, longitude) — set up here
 *              per request (one in-flight request at a time, mirroring iOS).
 *   - pwa:     navigator.geolocation.getCurrentPosition(...) (Web Geolocation).
 *
 * Callers use window.ChippieBridge.location() and await the coordinates, OR wire
 * a button with data-action="bridge--location#get" and listen for the dispatched
 * Stimulus events on this controller's element:
 *   - bridge--location:retrieved  detail: { latitude, longitude }
 *   - bridge--location:error
 *
 * Template wiring:
 *   <div data-controller="bridge--location"
 *        data-action="bridge--location:retrieved->job-form#fillAddress">
 *     <button data-action="bridge--location#get">Use my location</button>
 *   </div>
 *
 * The Android + PWA impls carry no per-instance state, so they are registered at
 * module load via registerStandalone() and ChippieBridge.location() resolves
 * everywhere. Only the ios impl needs a live Strada instance for this.send, so
 * connect() overrides it and disconnect() reverts to the rejecting placeholder.
 * Registering solely on connect (as this file used to) left
 * ChippieBridge.location UNDEFINED on PWA and desktop, where BridgeComponent's
 * UA-gated connect() never fires — callers got a TypeError, not a rejection.
 *
 * Web side adapted from joemasilotti/bridge-components location_controller.js;
 * extended with the ChippieBridge facade registration and the Android transport.
 */

import { BridgeComponent } from "@hotwired/hotwire-native-bridge";

// Global name the Android native side calls to deliver a result. Kept in sync
// with the Kotlin handler (ChippieNativeBridge.getLocation).
const ANDROID_RESULT_CALLBACK = "__chippieLocationResult";

// Every impl rejects with this same error so callers have one failure shape to
// branch on, whether the cause was denial, timeout or no transport at all.
function unavailable() {
  return Promise.reject(new Error("location-unavailable"));
}

// Android: call the @JavascriptInterface, which acquires the location off the
// binder thread and invokes the global result callback. One request in flight.
function androidLocation() {
  return new Promise((resolve, reject) => {
    window[ANDROID_RESULT_CALLBACK] = (latitude, longitude) => {
      delete window[ANDROID_RESULT_CALLBACK];
      if (latitude === null || longitude === null || latitude === undefined) {
        reject(new Error("location-unavailable"));
      } else {
        resolve({ latitude, longitude });
      }
    };
    try {
      window.ChippieNative.getLocation();
    } catch (_e) {
      delete window[ANDROID_RESULT_CALLBACK];
      reject(new Error("location-unavailable"));
    }
  });
}

// PWA: Web Geolocation API. enableHighAccuracy mirrors the native one-shot
// precise request.
function pwaLocation() {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error("location-unavailable"));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) =>
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        }),
      () => reject(new Error("location-unavailable")),
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  });
}

function registerStandalone() {
  if (!window.ChippieBridge || !window.ChippieBridge.register) return;
  window.ChippieBridge.register("location", {
    // ios is overridden by the controller's connect() with a real send; this
    // placeholder rejects rather than hanging if somehow selected before connect.
    ios: unavailable,
    android: androidLocation,
    pwa: pwaLocation,
  });
}

// Facade may load after this module; register as soon as both are present.
if (window.ChippieBridge) {
  registerStandalone();
} else {
  document.addEventListener("DOMContentLoaded", registerStandalone, { once: true });
}

export default class extends BridgeComponent {
  static component = "location";

  connect() {
    super.connect();
    if (!window.ChippieBridge || !window.ChippieBridge.register) return;
    window.ChippieBridge.register("location", {
      ios: () =>
        new Promise((resolve, reject) => {
          this.send("get", {}, (message) => {
            const { latitude, longitude } = message.data || {};
            if (latitude === undefined || longitude === undefined) {
              reject(new Error("location-unavailable"));
            } else {
              resolve({ latitude, longitude });
            }
          });
        }),
      android: androidLocation,
      pwa: pwaLocation,
    });
  }

  disconnect() {
    // Revert the live-send ios impl to the rejecting placeholder instead of
    // deleting the key — a call during Turbo navigation after disconnect would
    // otherwise throw TypeError, and Android/PWA would lose the capability too.
    registerStandalone();
    super.disconnect();
  }

  // data-action="bridge--location#get" trigger — fetch location, then dispatch
  // a Stimulus event other controllers/forms can react to.
  get(event) {
    if (event) {
      event.stopImmediatePropagation();
      event.preventDefault();
    }
    window.ChippieBridge.location().then(
      ({ latitude, longitude }) => {
        this.dispatch("retrieved", { detail: { latitude, longitude } });
      },
      () => {
        this.dispatch("error");
      }
    );
  }
}
